import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    Pagination,
    TextField,
    Typography
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { accessSupportChat, sendSupportMessage } from '../http/supportChatAPI';

const CHAT_COOKIE_NAME = 'support_chat_id';
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;
const MAX_MESSAGE_LENGTH = 1000;
const ANTISPAM_SECONDS = 15;
const MESSAGES_PER_PAGE = 30;

const getCookie = (name) => {
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
};

const setCookie = (name, value, days = 365) => {
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
};

const generateChatId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const formatMessageDate = (value) => {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }
    return parsed.toLocaleString('ru-RU');
};

const PageSupportChat = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { chatId } = useParams();

    const [email, setEmail] = useState('');
    const [activeEmail, setActiveEmail] = useState('');
    const [message, setMessage] = useState('');
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [error, setError] = useState('');
    const [cooldownLeft, setCooldownLeft] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    const isNewIntent = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get('new') === '1';
    }, [location.search]);

    const emailFromQuery = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return (params.get('email') || '').trim().toLowerCase();
    }, [location.search]);

    useEffect(() => {
        const existingChatId = getCookie(CHAT_COOKIE_NAME);

        if (existingChatId && existingChatId !== chatId) {
            navigate(`/support-chat/${existingChatId}`, { replace: true });
            return;
        }

        if (!chatId) {
            const newChatId = generateChatId();
            navigate(`/support-chat/${newChatId}?new=1`, { replace: true });
        }
    }, [chatId, navigate]);

    useEffect(() => {
        if (!chatId) {
            return;
        }

        const rememberedEmail = sessionStorage.getItem(`support_chat_email_${chatId}`);
        if (rememberedEmail) {
            setEmail(rememberedEmail);
        } else if (emailFromQuery) {
            setEmail(emailFromQuery);
        }

        setIsAuthorized(false);
        setActiveEmail('');
        setMessages([]);
        setCurrentPage(1);
        setTotalPages(1);
        setError('');
    }, [chatId, emailFromQuery]);

    const fetchMessages = useCallback(async (chatEmail, targetPage = 1) => {
        if (!chatId || !chatEmail) {
            return;
        }

        const data = await accessSupportChat(chatId, chatEmail, targetPage, MESSAGES_PER_PAGE);
        if (data.exists) {
            setMessages(data.messages || []);
            setCurrentPage(data.page || targetPage);
            setTotalPages(data.total_pages || 1);
            return;
        }

        if (!isNewIntent) {
            navigate('/support', { replace: true });
            return;
        }

        setMessages([]);
    }, [chatId, isNewIntent, navigate]);

    const handleAuthorize = async () => {
        const normalizedEmail = email.trim().toLowerCase();
        if (!normalizedEmail) {
            setError('Введите email для доступа к чату.');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const data = await accessSupportChat(chatId, normalizedEmail);

            if (!data.exists && !isNewIntent) {
                navigate('/support', { replace: true });
                return;
            }

            setIsAuthorized(true);
            setActiveEmail(normalizedEmail);
            setMessages(data.messages || []);
            setCurrentPage(data.page || 1);
            setTotalPages(data.total_pages || 1);
            sessionStorage.setItem(`support_chat_email_${chatId}`, normalizedEmail);
        } catch (e) {
            if (e.code === 'CHAT_ACCESS_DENIED') {
                navigate('/support', { replace: true });
                return;
            }

            setError(e.message || 'Не удалось открыть чат.');
        } finally {
            setLoading(false);
        }
    };

    const handleSendMessage = async () => {
        const trimmedMessage = message.trim();
        if (!trimmedMessage || !chatId || !activeEmail) {
            return;
        }

        if (trimmedMessage.length > MAX_MESSAGE_LENGTH) {
            setError('Сообщение не должно превышать 1000 символов.');
            return;
        }

        if (cooldownLeft > 0) {
            setError(`Следующее сообщение можно отправить через ${cooldownLeft} сек.`);
            return;
        }

        setSending(true);
        setError('');

        try {
            const data = await sendSupportMessage(chatId, activeEmail, trimmedMessage);
            setMessages(data.messages || []);
            setCurrentPage(data.page || 1);
            setTotalPages(data.total_pages || 1);
            setMessage('');
            setCookie(CHAT_COOKIE_NAME, chatId);
            setCooldownLeft(ANTISPAM_SECONDS);

            if (isNewIntent) {
                navigate(`/support-chat/${chatId}`, { replace: true });
            }
        } catch (e) {
            if (e.code === 'CHAT_ACCESS_DENIED') {
                navigate('/support', { replace: true });
                return;
            }

            if (e.code === 'CHAT_RATE_LIMIT') {
                setCooldownLeft(Math.max(Number(e.retry_after_seconds) || ANTISPAM_SECONDS, 1));
            }

            setError(e.message || 'Не удалось отправить сообщение.');
        } finally {
            setSending(false);
        }
    };

    useEffect(() => {
        if (cooldownLeft <= 0) {
            return;
        }

        const timer = setInterval(() => {
            setCooldownLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }

                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [cooldownLeft]);

    useEffect(() => {
        if (!isAuthorized || !activeEmail || !chatId) {
            return;
        }

        const intervalId = setInterval(() => {
            fetchMessages(activeEmail, currentPage).catch(() => {});
        }, REFRESH_INTERVAL_MS);

        return () => {
            clearInterval(intervalId);
        };
    }, [isAuthorized, activeEmail, chatId, fetchMessages, currentPage]);

    const handleChangePage = async (_, page) => {
        if (!activeEmail) {
            return;
        }

        setError('');
        try {
            await fetchMessages(activeEmail, page);
        } catch (e) {
            setError(e.message || 'Не удалось загрузить сообщения.');
        }
    };

    if (!chatId) {
        return (
            <Card>
                <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="center" minHeight="30vh">
                        <CircularProgress />
                    </Box>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardContent>
                <Box display="flex" flexDirection="column" gap={2} minHeight="60vh">
                    <Typography align="center" variant="h5">
                        Чат с поддержкой
                    </Typography>

                    <Typography align="center" variant="body2" color="text.secondary">
                        ID чата: {chatId}
                    </Typography>

                    {!isAuthorized && (
                        <Box display="flex" flexDirection="column" gap={2} maxWidth={420} mx="auto" width="100%">
                            <Typography align="center" variant="body2">
                                Введите email, который будете использовать для этого чата.
                            </Typography>
                            <FormControl fullWidth>
                                <TextField
                                    label="Email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                />
                            </FormControl>
                            <Button variant="contained" onClick={handleAuthorize} disabled={loading}>
                                {loading ? 'Проверка...' : 'Открыть чат'}
                            </Button>
                        </Box>
                    )}

                    {isAuthorized && (
                        <>
                            <Box
                                sx={{
                                    border: '1px solid rgba(255,255,255,0.12)',
                                    borderRadius: 1,
                                    p: 2,
                                    minHeight: 280,
                                    maxHeight: 420,
                                    overflowY: 'auto',
                                    backgroundColor: '#1a1a1a'
                                }}
                            >
                                {messages.length === 0 && (
                                    <Typography variant="body2" color="text.secondary">
                                        Чат создан. Отправьте первое сообщение в поддержку.
                                    </Typography>
                                )}

                                {messages.map((item, index) => {
                                    const isUser = item.who_send === 'user';
                                    return (
                                        <Box
                                            key={`${item.date}-${index}`}
                                            display="flex"
                                            justifyContent={isUser ? 'flex-end' : 'flex-start'}
                                            mb={1.5}
                                        >
                                            <Box
                                                sx={{
                                                    p: 1.25,
                                                    borderRadius: 1,
                                                    maxWidth: '75%',
                                                    backgroundColor: isUser ? '#2f5dff' : '#2f2f2f'
                                                }}
                                            >
                                                <Typography variant="body2">{item.message}</Typography>
                                                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                                    {item.who_send} • {formatMessageDate(item.date)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>

                            <Box display="flex" gap={1} alignItems="stretch">
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    maxRows={5}
                                    label="Введите сообщение"
                                    value={message}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value.length <= MAX_MESSAGE_LENGTH) {
                                            setMessage(value);
                                        }
                                    }}
                                    helperText={`${message.length}/${MAX_MESSAGE_LENGTH}${cooldownLeft > 0 ? ` • можно отправить через ${cooldownLeft} сек.` : ''}`}
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleSendMessage}
                                    disabled={sending || !message.trim() || cooldownLeft > 0}
                                >
                                    {sending ? '...' : cooldownLeft > 0 ? `Ждите ${cooldownLeft}с` : 'Отправить'}
                                </Button>
                            </Box>

                            {totalPages > 1 && (
                                <Box display="flex" justifyContent="center" mt={1}>
                                    <Pagination
                                        count={totalPages}
                                        page={currentPage}
                                        onChange={handleChangePage}
                                        color="primary"
                                        shape="rounded"
                                    />
                                </Box>
                            )}
                        </>
                    )}

                    {error && (
                        <Typography align="center" color="error" variant="body2">
                            {error}
                        </Typography>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

export default PageSupportChat;
